import { Component, OnInit, ViewChild, HostListener } from '@angular/core';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { StyleClass } from 'primeng/styleclass';
import { Drawer } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';
import { MenuItem, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { AuthService } from '../../core/services/auth';
import { EncryptionService } from '../../core/services/encryption';
import { SETTINGS } from '../../core/config/common.settings';
import { ChargebeeService } from '../../core/services/chargebee.service';
import { SubscriberService } from '../../core/services/subscriber.service';
import { SubscriptionSelectorComponent } from '../../components/subscription-selector/subscription-selector.component';
import { SubscriptionContextService } from '../../core/services/subscription-context.service';
import { PermissionService } from '../../core/services/permission.service';
import { NotificationPanelComponent } from '../../shared/components/notification-panel/notification-panel.component';
import { FocusManagementService } from '../../core/services/focus-management.service';
import { SrAnnouncerComponent } from '../../shared/components/sr-announcer/sr-announcer.component';
import { PersistentErrorDisplayComponent } from '../../components/persistent-error-display/persistent-error-display.component';
import { ScrollService } from '../../core/services/scroll.service';
import { GlobalLoadingService } from '../../core/services/global-loading.service';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
@Component({
  selector: 'app-admin-layout',
  imports: [
    DrawerModule,
    RippleModule,
    CardModule,
    MenuModule,
    ButtonModule,
    AvatarModule,
    CommonModule,
    RouterOutlet,
    SubscriptionSelectorComponent,
    ToastModule,
    NotificationPanelComponent,
    SrAnnouncerComponent,
    PersistentErrorDisplayComponent,
    ProgressSpinnerModule,
    TooltipModule,
  ],
  templateUrl: './admin-layout.html',
  styleUrl: './admin-layout.scss',
  providers: [MessageService],
  animations: [
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ opacity: 0, transform: 'translateX(-20px)' })
        ),
      ]),
    ]),
    trigger('fadeSlideInOutSlow', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate(
          '600ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ opacity: 0, transform: 'translateX(-20px)' })
        ),
      ]),
    ]),
  ],
})
export class AdminLayout implements OnInit {
  items: MenuItem[] | undefined;
  drawerItems: MenuItem[] | undefined;
  isVisibleDrawer: boolean = false;
  isVisibleSidebar: boolean;
  isUserDropdownOpen: boolean = false;
  version: string = '1.0.0';
  
  // Loading states
  isLoadingMenu: boolean = true;
  isLoadingPermissions: boolean = true;

  user: any = null;
  loggedInUserName: string = '';
  loggedInUserFirstName: string = '';
  loggedInUserLastName: string = '';
  loggedInUserEmail: string = '';
  loggedInUserRole: string = '';
  loggedInUserRoleName: string = '';
  loggedInUserID: string = '';
  loggedInUserSubscriberId: string = '';
  navigationItems: MenuItem[] | undefined;

  // Subscription selector properties
  showSubscriptionSelector: boolean = false;
  selectedSubscriberId: string | null = null;
  hasSelectedSubscription: boolean = false;

  // Global loading observable
  isGlobalLoading$!: Observable<boolean>;

  constructor(
    private authService: AuthService,
    private encryption: EncryptionService,
    private router: Router,
    private messageService: MessageService,
    private chargebeeService: ChargebeeService,
    private subscriberService: SubscriberService,
    private subscriptionContextService: SubscriptionContextService,
    private permissionService: PermissionService,
    private focusManagementService: FocusManagementService,
    private scrollService: ScrollService,
    private globalLoadingService: GlobalLoadingService
  ) {
    this.isVisibleSidebar = !this.isMobile();
    this.isGlobalLoading$ = this.globalLoadingService.isLoading$;
  }

  private isMobile(): boolean {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  }

  ngOnInit() {
    // Ensure page starts at the top
    this.scrollService.scrollToTop();
    
    // Check authentication first - this will update localStorage and component properties with fresh data
    // loadCurrentUser() is called as a fallback in case authentication check hasn't completed yet
    this.checkAuthentication();
    // Load from localStorage as initial state (will be updated by checkAuthentication if fresh data is available)
    this.loadCurrentUser();

    // Subscribe to permission changes to update menu
    this.permissionService.userPermissions$.subscribe(() => {
      this.configureMenu();
      // Update menu active state after menu is configured
      this.refreshMenuAfterLoad();
    });

    // Subscribe to subscription context changes to update menu
    this.subscriptionContextService.selectedSubscriber$.subscribe((context) => {
      this.hasSelectedSubscription = this.subscriptionContextService.isSubscriberSelected();
      this.configureMenu();
      // Update menu active state after menu is configured
      this.refreshMenuAfterLoad();
    });

    if (
      this.loggedInUserRole === 'SA' ||
      this.loggedInUserRole === 'A' ||
      this.loggedInUserRole === 'FSR'
    ) {
      this.findSubscriberPayment().then(async (r: any) => {
        const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
        // console.log(encryptedUserStr);

        if (encryptedUserStr) {
          const userStr = this.encryption.decrypt(encryptedUserStr);
          // console.log(userStr);
          const user = JSON.parse(userStr);
          const subscriberData = r?.subscriber ?? r;
          const payment = subscriberData?.payment;
          const subList = payment?.customerId
            ? await this.chargebeeService.getSubscriptionByCustomerId(
                payment.customerId
              )
            : [];
          
          // Check if subscription exists and is active
          const hasActiveSubscription = subList?.length > 0 && 
            subList[0].status?.toLowerCase() === 'active';
          
          // Only redirect if subscription is NOT active
          if (!hasActiveSubscription) {
            if (subList?.length > 0) {
              // Subscription exists but is not active (cancelled, expired, etc.)
              if (subList[0].status === 'cancelled') {
                this.determineRedirect(user, '/payment/portal');
              } else {
                // Other non-active statuses (expired, non_renewing, etc.)
                this.determineRedirect(user, '/payment/portal');
              }
            } else {
              // No subscription found
              this.determineRedirect(user, '/payment');
            }
          }
          // If subscription is active, do nothing - allow user to continue
        }
      });
    }

    // Check if user should see subscription selector (high-level roles)
    this.checkSubscriptionSelectorVisibility();

    // Initialize subscription state
    this.hasSelectedSubscription = this.subscriptionContextService.isSubscriberSelected();

    this.configureMenu();
    this.trackCurrentRoute();
    
    // Initialize menu active state after page load - use the robust refresh method
    this.refreshMenuAfterLoad();
    
    this.navigationItems = [
      {
        label: 'My Profile',
        icon: 'pi pi-user',
        command: () => this.navigateToProfile(),
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
        styleClass: 'p-menuitem-danger',
      },
    ];
  }

  private checkAuthentication(): void {
    // Verify authentication with backend
    // If the request fails due to expired token, try to refresh first
    this.authService.getCurrentUser().pipe(
      catchError((error) => {
        // If authentication fails (401/403), try to refresh the token first
        // This handles the case where the access token expired but refresh token is still valid
        if (error.status === 401 || error.status === 403) {
          console.log('Authentication check failed, attempting token refresh...');
          return this.authService.refreshToken().pipe(
            switchMap(() => {
              // Token refresh successful, retry getCurrentUser
              console.log('Token refreshed successfully, retrying authentication check...');
              return this.authService.getCurrentUser();
            }),
            catchError((refreshError) => {
              // Refresh also failed, user needs to login again
              console.error('Token refresh failed:', refreshError);
              return throwError(() => refreshError);
            })
          );
        }
        // For other errors, throw them as-is
        return throwError(() => error);
      })
    ).subscribe({
      next: (response: any) => {
        // User is authenticated, continue
        const user = response.user || response; // Handle both response structures
        console.log('User authenticated:', user);
        
        // CRITICAL FIX: Update localStorage with fresh user data from server
        // This ensures that after a deployment, user data (especially role) is refreshed
        if (user) {
          // Normalize the user object structure for localStorage
          // Handle both 'role' and 'roleId' structures for backward compatibility
          // Server returns 'roleId', but we store as 'role' for consistency with existing code
          const normalizedUser = {
            ...user,
            // Ensure role structure is consistent - prefer roleId from server, fallback to role
            // Store as 'role' to maintain backward compatibility with existing localStorage structure
            role: user.roleId || user.role,
            // Preserve roleId as well for components that might use it
            roleId: user.roleId || user.role,
            // Preserve other important fields
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            userName: user.userName || user.email,
            email: user.email,
            subscriberId: user.subscriberId,
            status: user.status,
            isFirstLogin: user.isFirstLogin,
            accountStatus: user.accountStatus || user.status,
            accountSetupStep: user.accountSetupStep
          };
          
          // Update localStorage with fresh user data
          this.authService.setLoggedInUser(JSON.stringify(normalizedUser));
          
          // Update component properties with fresh data
          this.updateUserProperties(normalizedUser);
        }
        
        // Refresh subscription context after successful authentication
        this.subscriptionContextService.refreshFromCurrentUser();
      },
      error: (error) => {
        console.error('Authentication check failed after token refresh attempt:', error);
        // If authentication fails even after refresh attempt, logout and redirect to login
        this.authService.logout();
      },
    });
  }

  private trackCurrentRoute(): void {
    // Subscribe to route changes to update menu highlighting
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log('Navigation ended to:', event.url);
        this.updateMenuActiveState();
        this.updateSidebarVisibilityForRoute();
        // Scroll to top on route change
        this.scrollService.scrollToTop();
      }
    });

    // Apply sidebar visibility for initial route (e.g. when landing or refreshing on add-territory)
    this.updateSidebarVisibilityForRoute();

    // Note: Initial call to set active state is now handled in ngOnInit with proper timing
  }

  /**
   * Hide main sidebar on routes that need full width (e.g. add-territory).
   */
  private updateSidebarVisibilityForRoute(): void {
    const url = this.router.url;
    if (url.includes('territory-management/add')) {
      this.isVisibleSidebar = false;
    }
  }

  /**
   * Method to store intended route before 2FA redirect
   * Call this method before redirecting user to two-factor authentication
   * Example usage in AuthService or 2FA component:
   * 
   * // Before redirecting to 2FA
   * this.adminLayout.storeIntendedRouteFor2FA();
   * this.router.navigate(['/two-factor-auth']);
   */
  public storeIntendedRouteFor2FA(): void {
    const currentRoute = this.router.url;
    if (currentRoute && currentRoute !== '/login' && currentRoute !== '/two-factor-auth') {
      sessionStorage.setItem('intendedRouteAfter2FA', currentRoute);
      console.log('Stored intended route for 2FA:', currentRoute);
    }
  }

  /**
   * Method to handle return from 2FA - should be called after successful 2FA
   * This method will navigate back to the intended route and update menu highlighting
   * Example usage in 2FA component after successful verification:
   * 
   * // After successful 2FA verification
   * this.adminLayout.handleReturnFrom2FA();
   */
  public handleReturnFrom2FA(): void {
    const intendedRoute = sessionStorage.getItem('intendedRouteAfter2FA');
    if (intendedRoute) {
      console.log('Returning from 2FA to intended route:', intendedRoute);
      // Navigate to the intended route
      this.router.navigate([intendedRoute]).then(() => {
        // Update menu active state after navigation
        setTimeout(() => {
          this.updateMenuActiveState();
        }, 100);
      });
    } else {
      // No intended route, just update the current menu state
      this.updateMenuActiveState();
    }
  }

  /**
   * Method to force update menu active state - useful for external components
   * Call this method when you need to manually refresh the menu highlighting
   * Example usage:
   * 
   * // Force update menu highlighting
   * this.adminLayout.forceUpdateMenuActiveState();
   */
  public forceUpdateMenuActiveState(): void {
    this.updateMenuActiveState();
  }

  /**
   * Method to refresh menu after page load - ensures active state is properly set
   * This method should be called after the page has fully loaded and menu is configured
   */
  public refreshMenuAfterLoad(): void {
    // Wait for menu to be fully configured, then update active state
    setTimeout(() => {
      if (this.items && this.drawerItems) {
        this.updateMenuActiveState();
      } else {
        // If menu is not ready, try again after a short delay
        setTimeout(() => this.refreshMenuAfterLoad(), 100);
      }
    }, 100);
  }

  private updateMenuActiveState(): void {
    const currentUrl = this.router.url;
    console.log('Updating menu active state for URL:', currentUrl);

    // Check if menu items are properly initialized
    if (!this.items || !this.drawerItems) {
      console.log('Menu items not yet initialized, skipping active state update');
      return;
    }

    console.log('Menu items are initialized, updating active state...');

    // Update sidebar menu items to show active state
    this.items.forEach((item) => {
      if (item.items) {
        item.items.forEach((subItem) => {
          if (subItem.routerLink) {
            // Remove any existing active styling
            subItem.styleClass = '';

            // Add active styling if current route matches exactly or starts with the route
            if (this.isRouteActive(subItem.routerLink, currentUrl)) {
              subItem.styleClass = 'active-menu-item';
              console.log('Setting active menu item:', subItem.label, 'for route:', subItem.routerLink);
            }
          }
        });
      }
    });

    // Update drawer menu items to show active state
    this.drawerItems.forEach((item) => {
      if (item.items) {
        item.items.forEach((subItem) => {
          // Remove any existing active styling
          subItem.styleClass = '';

          // For drawer items, check the stored route data
          if (subItem['data']?.route) {
            const route = subItem['data'].route;
            if (this.isRouteActive(route, currentUrl)) {
              subItem.styleClass = 'active-menu-item';
              console.log('Setting active drawer menu item:', subItem.label, 'for route:', route);
            }
          }
        });
      }
    });
    
    // Force change detection to update both sidebar and drawer UI
    this.items = [...this.items];
    this.drawerItems = [...this.drawerItems];
    
    console.log('Menu active state update completed');
  }

  private isRouteActive(menuRoute: string, currentUrl: string): boolean {
    // Handle exact matches
    if (currentUrl === menuRoute) {
      return true;
    }

    // Handle sub-routes (e.g., /admin/users matches /admin/users/123)
    if (currentUrl.startsWith(menuRoute + '/')) {
      return true;
    }

    // Handle special cases for two-factor auth redirection
    // If user was redirected from 2FA, they might be on a different route
    // but we want to highlight the intended destination
    const intendedRoute = this.getIntendedRouteAfter2FA();
    if (intendedRoute && intendedRoute === menuRoute) {
      return true;
    }

    // Handle query parameters (e.g., /admin/users?page=1 should match /admin/users)
    const currentPath = currentUrl.split('?')[0];
    const menuPath = menuRoute.split('?')[0];
    
    if (currentPath === menuPath) {
      return true;
    }

    // Handle fragment identifiers (e.g., /admin/users#section should match /admin/users)
    const currentPathNoFragment = currentPath.split('#')[0];
    const menuPathNoFragment = menuPath.split('#')[0];
    
    if (currentPathNoFragment === menuPathNoFragment) {
      return true;
    }

    return false;
  }

  private getIntendedRouteAfter2FA(): string | null {
    // Check if there's a stored intended route after 2FA
    const intendedRoute = sessionStorage.getItem('intendedRouteAfter2FA');
    if (intendedRoute) {
      // Clear it after use
      sessionStorage.removeItem('intendedRouteAfter2FA');
      return intendedRoute;
    }
    return null;
  }

  private updateMenuLoadingState(): void {
    // Menu is loaded when permissions are loaded and subscription context is ready
    this.isLoadingMenu = this.isLoadingPermissions;
  }

  private configureMenu(): void {
    this.isLoadingMenu = this.isLoadingPermissions;
    
    const allMenuItems = [
      {
        label: 'Overview',
        items: [
          {
            label: 'Admin Dashboard',
            icon: 'pi pi-chart-line',
            routerLink: '/admin/admin-dashboard',
            command: () => this.closeDrawerAndNavigate('/admin/admin-dashboard'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'FSR Dashboard',
            icon: 'pi pi-chart-bar',
            routerLink: '/admin/fsr-dashboard',
            command: () => this.closeDrawerAndNavigate('/admin/fsr-dashboard'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },

        ],
      },
      {
        separator: true,
      },
      {
        label: 'User Management',
        items: [
          {
            label: 'Add New User',
            icon: 'pi pi-user-plus',
            shortcut: '⌘+O',
            routerLink: '/admin/add-edit-user',
            command: () => this.closeDrawerAndNavigate('/admin/add-edit-user'),
          },
          {
            label: 'Users',
            icon: 'pi pi-users',
            routerLink: '/admin/user-management',
            command: () => this.closeDrawerAndNavigate('/admin/user-management'),
          },
          {
            label: 'Roles',
            icon: 'pi pi-sparkles',
            routerLink: '/admin/role-management',
            command: () => this.closeDrawerAndNavigate('/admin/role-management'),
          },
          {
            label: 'Base Roles',
            icon: 'pi pi-shield',
            routerLink: '/admin/base-role-management',
            command: () => this.closeDrawerAndNavigate('/admin/base-role-management'),

          },
          // {
          //   label: 'Add New Customer',
          //   icon: 'pi pi-plus',
          //   routerLink: '/admin/add-edit-customer',
          //   command: () => this.closeCallback(7),
          // },
          // {
          //   label: 'Add New Reservation',
          //   icon: 'pi pi-plus',
          //   routerLink: '/admin/add-reservation',
          //   command: () => this.closeCallback(8),
          // },
         
          // {
          //   label: 'Add New Contract',
          //   icon: 'pi pi-plus',
          //   routerLink: '/admin/add-edit-contract',
          //   command: () => this.closeCallback(10),
          // }
        ],
      },

     
      {
        separator: true,
      },
      {
        label: 'Business Operations',
        items: [
          {
            label: 'Calendar',
            icon: 'pi pi-calendar',
            routerLink: '/admin/calendar',
            command: () => this.closeDrawerAndNavigate('/admin/calendar'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Customer Lookup',
            icon: 'pi pi-search',
            routerLink: '/admin/customer-lookup',
            command: () => this.closeDrawerAndNavigate('/admin/customer-lookup'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Customer Management',
            icon: 'pi pi-users',
            routerLink: '/admin/customers',
            command: () => this.closeDrawerAndNavigate('/admin/customers'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Product Management',
            icon: 'pi pi-box',
            routerLink: '/admin/product-management',
            command: () => this.closeDrawerAndNavigate('/admin/product-management'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Contract Management',
            icon: 'pi pi-file',
            routerLink: '/admin/contract-management',
            command: () => this.closeDrawerAndNavigate('/admin/contract-management'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Territory Management',
            icon: 'pi pi-map',
            routerLink: '/admin/territory-management',
            command: () => this.closeDrawerAndNavigate('/admin/territory-management'),
            visible: this.hasSelectedSubscription || this.loggedInUserRole === 'FSR', // Always show for FSR users
          },
          {
            label: 'Reports',
            icon: 'pi pi-chart-line',
            routerLink: '/admin/simple-reports',
            command: () => this.closeDrawerAndNavigate('/admin/simple-reports'),
            visible: this.hasSelectedSubscription
          },
         
          
        
        ],
      },

      {
        separator: true,
      },
      {
        label: 'Subscription & Billing',
        items: [
          {
            label: 'Subscription',
            icon: 'pi pi-credit-card',
            routerLink: '/admin/payment/portal',
            command: () => this.closeDrawerAndNavigate('/admin/payment/portal'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Subscribers',
            icon: 'pi pi-users',
            routerLink: '/admin/subscriber-management',
            command: () => this.closeDrawerAndNavigate('/admin/subscriber-management'),
          },
          {
            label: 'Company Configuration',
            icon: 'pi pi-cog',
            routerLink: '/admin/company-configuration',
            command: () => this.closeDrawerAndNavigate('/admin/company-configuration'),
            visible: this.hasSelectedSubscription, // Hide when no subscription selected
          },
          {
            label: 'Messaging & Notifications',
            icon: 'pi pi-bell',
            routerLink: '/admin/message-template',
            command: () => this.closeDrawerAndNavigate('/admin/message-template'),
          },
          
        ],
      },
      {
        separator: true,
      },
      {
        label: 'Help & Support',
        items: [
          {
            label: 'Handbook & FAQs',
            icon: 'pi pi-book',
            routerLink: '/admin/online-handbook',
            command: () => this.closeDrawerAndNavigate('/admin/online-handbook'),
          },
          {
            label: 'Audit Logs',
            icon: 'pi pi-history',
            routerLink: '/admin/system-logs',
            command: () => this.closeDrawerAndNavigate('/admin/system-logs'),
          },
         
        ],
      },
    ];

    // Filter menu items based on user permissions
    this.items = this.permissionService.getFilteredMenuItems(allMenuItems);
    
    // Create drawer-specific menu items with proper navigation handling
    this.drawerItems = this.createDrawerMenuItems(allMenuItems);
    
    // Mark menu as loaded if permissions are ready
    if (!this.isLoadingPermissions) {
      this.isLoadingMenu = false;
    }
  }

  private navigateTo(route: string): void {
    this.router.navigate([route]);
    // Close drawer on mobile after navigation
    if (this.isMobile()) {
      this.isVisibleDrawer = false;
    }
    // Scroll to top after navigation
    setTimeout(() => this.scrollService.scrollToTop(), 100);
  }

  isActiveRoute(route: string): boolean {
    return this.router.url === route;
  }

  @ViewChild('drawerRef') drawerRef!: Drawer;

  closeCallback(e: any): void {
    this.drawerRef.close(e);
  }

  closeDrawerAndNavigate(route: string): void {
    // Close the drawer
    this.isVisibleDrawer = false;
    // Navigate to the route
    this.router.navigate([route]).then(() => {
      // Update menu active state after navigation is complete
      setTimeout(() => {
        this.updateMenuActiveState();
        this.scrollService.scrollToTop();
      }, 100);
    });
  }

  onDrawerShow(): void {
    // Refresh menu active state when drawer opens
    setTimeout(() => {
      this.updateMenuActiveState();
    }, 50);
  }

  private createDrawerMenuItems(allMenuItems: any[]): MenuItem[] {
    const filteredItems = this.permissionService.getFilteredMenuItems(allMenuItems);
    
    // Deep clone and modify menu items for drawer
    const drawerItems = JSON.parse(JSON.stringify(filteredItems));
    
    // Update all menu items to use drawer-specific navigation
    drawerItems.forEach((item: any) => {
      if (item.items) {
        item.items.forEach((subItem: any) => {
          if (subItem.routerLink) {
            // Store the route for active state detection
            subItem.data = { route: subItem.routerLink };
            // Remove routerLink and use only command for drawer
            const route = subItem.routerLink;
            delete subItem.routerLink;
            subItem.command = () => this.closeDrawerAndNavigate(route);
          }
        });
      }
    });
    
    return drawerItems;
  }

  loadCurrentUser(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    // console.log(encryptedUserStr);

    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      // console.log(userStr);
      const user = JSON.parse(userStr);
      this.updateUserProperties(user);

      // Load user permissions from server
      this.loadUserPermissions();
    }
  }

  /**
   * Update component properties with user data
   * Handles both 'role' and 'roleId' structures for backward compatibility
   */
  private updateUserProperties(user: any): void {
    this.loggedInUserName = user.userName || user.email;
    this.loggedInUserFirstName = user.firstName;
    this.loggedInUserLastName = user.lastName;
    this.loggedInUserEmail = user.email;
    this.loggedInUserID = user._id;
    
    // Handle role extraction - support both 'role' and 'roleId' structures
    // Server returns 'roleId', but localStorage might have 'role' for backward compatibility
    const roleData = user.roleId || user.role;
    
    if (roleData) {
      if (typeof roleData === 'object') {
        // Role is an object with identification and name
        this.loggedInUserRole = roleData.identification || roleData._id || '';
        this.loggedInUserRoleName = roleData.name || '';
      } else {
        // Role is a string (legacy format)
        this.loggedInUserRole = roleData;
        this.loggedInUserRoleName = '';
      }
    } else {
      // No role data found - this should not happen, but handle gracefully
      console.warn('AdminLayout: No role data found in user object:', user);
      this.loggedInUserRole = '';
      this.loggedInUserRoleName = '';
    }
    
    // Handle subscriberId
    this.loggedInUserSubscriberId =
      typeof user.subscriberId === 'object'
        ? user.subscriberId?._id
        : user.subscriberId;
  }

  private loadUserPermissions(): void {
    this.isLoadingPermissions = true;
    this.permissionService.refreshPermissions().subscribe({
      next: (permissions) => {
        console.log('AdminLayout: Loaded user permissions:', permissions);
        this.permissionService.setUserPermissions(permissions);
        this.isLoadingPermissions = false;
        this.updateMenuLoadingState();
      },
      error: (error) => {
        console.error('AdminLayout: Failed to load user permissions:', error);
        // If permissions fail to load, configure menu with empty permissions
        this.permissionService.setUserPermissions({
          permissions: [],
          role: { identification: this.loggedInUserRole, name: '', level: '' }
        });
        this.isLoadingPermissions = false;
        this.updateMenuLoadingState();
      }
    });
  }

  private checkSubscriptionSelectorVisibility(): void {
    // Show subscription selector for high-level roles that can manage multiple subscriptions
    const highLevelRoles = ['SUPER_DEV_ADMIN', 'CSM', 'SM'];
    this.showSubscriptionSelector = highLevelRoles.includes(
      this.loggedInUserRole
    );
  }

  onSubscriptionChanged(subscriberId: string | null): void {
    this.selectedSubscriberId = subscriberId;
    this.hasSelectedSubscription = this.subscriptionContextService.isSubscriberSelected();

    // Use the subscription context service to manage the selected subscriber
    this.subscriptionContextService.setSelectedSubscriber(subscriberId);

    console.log('Subscription context changed to:', subscriberId);
  }

  getRoleBasedColor(role: string): string {
    switch (role) {
      case 'CSM':
        return 'bg-purple-600';
      case 'SM':
        return 'bg-blue-600';
      case 'SA':
        return 'bg-green-600';
      case 'A':
        return 'bg-red-600';
      case 'FSR':
        return 'bg-yellow-600';
      case 'SUPER_DEV_ADMIN':
        return 'bg-indigo-600';
      default:
        return 'bg-gray-600';
    }
  }

  getRoleName(role: string): string {
    switch (role) {
      case 'CSM':
        return 'Chief System Manager';
      case 'SM':
        return 'System Manager';
      case 'SA':
        return 'Super Admin';
      case 'A':
        return 'Administrator';
      case 'FSR':
        return 'Field Service Representative';
      case 'SUPER_DEV_ADMIN':
        return 'Super Dev Admin';
      default:
        return 'Custom Role';
    }
  }

  getUserInitials(): string {
    const firstInitial = this.loggedInUserFirstName
      ? this.loggedInUserFirstName.charAt(0).toUpperCase()
      : '';
    const lastInitial = this.loggedInUserLastName
      ? this.loggedInUserLastName.charAt(0).toUpperCase()
      : '';
    return firstInitial + lastInitial;
  }

  logout() {
    this.authService.logout();
  }

  toggleUserDropdown() {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
  }

  closeUserDropdown() {
    this.isUserDropdownOpen = false;
  }

  navigateToProfile(): void {
    this.router.navigate(['/admin/me']).then(() => {
      this.closeUserDropdown();
      // Update menu active state after navigation
      setTimeout(() => {
        this.updateMenuActiveState();
        this.scrollService.scrollToTop();
      }, 100);
    });
  }

  onLogout() {
    this.closeUserDropdown();
    this.logout();
  }

  refreshPage(): void {
    window.location.reload();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-dropdown-container')) {
      this.closeUserDropdown();
    }
  }

  async findSubscriberPayment(): Promise<any> {
    try {
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      // console.log(encryptedUserStr);

      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        // console.log(userStr);
        const user = JSON.parse(userStr);
        const subscriberId =
          typeof user.subscriberId === 'object'
            ? user.subscriberId?._id
            : user.subscriberId;
        return await this.subscriberService.findSubscriberPayment(subscriberId);
      }
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: e.message,
      });
    }
  }

  determineRedirect(user: any, redirectTo: string): void {
    const roleIdent =
      typeof user.role === 'object' ? user.role?.identification : user.role;
    if (roleIdent === 'SA') {
      this.router.navigate([redirectTo]);
    } else if (roleIdent === 'FSR') {
      // For FSR users, show warning but don't logout - allow them to continue working
      this.messageService.add({
        severity: 'warn',
        summary: 'Subscription Issue',
        detail: 'There may be an issue with your subscription. Some features might be limited. Please contact your administrator.',
        life: 8000
      });
      // Don't logout FSR users, let them continue to work
    } else {
      // For other non-SA users (Admin, etc.), show error and logout
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Issue with Subscription: Please contact Super Admin',
      });
      this.authService.logout();
    }
  }
}
